import mapboxgl from 'mapbox-gl'
import * as turf from '@turf/turf'
/**
 * Utility functions for working with map bounding boxes
 */

export interface BoundingBoxConfig {
  /** The bounding box to fit */
  bbox: mapboxgl.LngLatBoundsLike
  /** Padding around the bounding box */
  padding?: mapboxgl.PaddingOptions | number
  /** Animation duration in milliseconds */
  duration?: number
  /** Whether to animate the transition */
  animate?: boolean
  /** Maximum zoom level to apply */
  maxZoom?: number
}

/**
 * Creates a bounding box from a center point and radius
 */
export const createBoundingBoxFromCenter = (
  center: [number, number],
  radiusInMeters: number = 1000
): mapboxgl.LngLatBoundsLike => {
  const [lng, lat] = center

  // Approximate conversion from meters to degrees
  // This is approximate and works best for small areas
  const latDelta = radiusInMeters / 111320 // 1 degree lat ≈ 111320 meters
  const lngDelta = radiusInMeters / (111320 * Math.cos((lat * Math.PI) / 180))

  return [
    [lng - lngDelta, lat - latDelta], // Southwest
    [lng + lngDelta, lat + latDelta], // Northeast
  ]
}

/**
 * Creates a bounding box from multiple points
 */
export const createBoundingBoxFromPoints = (
  points: Array<[number, number]>
): mapboxgl.LngLatBoundsLike | null => {
  if (points.length === 0) return null

  let minLng = points[0][0]
  let minLat = points[0][1]
  let maxLng = points[0][0]
  let maxLat = points[0][1]

  for (const [lng, lat] of points) {
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  }

  return [
    [minLng, minLat], // Southwest
    [maxLng, maxLat], // Northeast
  ]
}

/**
 * Expands a bounding box by a percentage
 */
export const expandBoundingBox = (
  bbox: mapboxgl.LngLatBoundsLike,
  expansionPercent: number = 10
): mapboxgl.LngLatBoundsLike => {
  const bounds = Array.isArray(bbox)
    ? new mapboxgl.LngLatBounds(bbox[0] as any, bbox[1] as any)
    : (bbox as mapboxgl.LngLatBounds)
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()

  const lngRange = ne.lng - sw.lng
  const latRange = ne.lat - sw.lat

  const lngExpansion = (lngRange * expansionPercent) / 100
  const latExpansion = (latRange * expansionPercent) / 100

  return [
    [sw.lng - lngExpansion, sw.lat - latExpansion], // Southwest
    [ne.lng + lngExpansion, ne.lat + latExpansion], // Northeast
  ]
}

/**
 * Fits the map to a bounding box with options
 */
export const fitMapToBounds = (
  map: mapboxgl.Map,
  config: BoundingBoxConfig
): void => {
  if (!map) return

  const {
    bbox,
    padding = 50,
    duration = 1000,
    animate = true,
    maxZoom = 16,
  } = config

  const options: mapboxgl.FitBoundsOptions = {
    padding,
    maxZoom,
  }

  if (animate) {
    options.duration = duration
  }

  map.fitBounds(bbox, options)
}

/**
 * Gets the current map bounds
 */
export const getCurrentBounds = (
  map: mapboxgl.Map
): mapboxgl.LngLatBoundsLike => {
  return map?.getBounds?.()?.toArray() as [[number, number], [number, number]]
}

/**
 * Checks if a point is within a bounding box
 */
export const isPointInBounds = (
  point: [number, number],
  bbox: mapboxgl.LngLatBoundsLike
): boolean => {
  const bounds = Array.isArray(bbox)
    ? new mapboxgl.LngLatBounds(bbox[0] as any, bbox[1] as any)
    : (bbox as mapboxgl.LngLatBounds)
  return bounds.contains(point)
}

/**
 * Calculates the center point of a bounding box
 */
export const getBoundingBoxCenter = (
  bbox: mapboxgl.LngLatBoundsLike
): [number, number] => {
  const bounds = Array.isArray(bbox)
    ? new mapboxgl.LngLatBounds(bbox[0] as any, bbox[1] as any)
    : (bbox as mapboxgl.LngLatBounds)
  const center = bounds.getCenter()
  return [center.lng, center.lat]
}

/**
 * Creates a bounding box from GeoJSON geometry using Turf.js
 */
export const createBoundingBoxFromGeoJSON = (
  geoJson: any
): mapboxgl.LngLatBoundsLike | null => {
  if (!geoJson) return null

  try {
    // Handle different input formats
    let geometry: any = geoJson

    // If it's a raw coordinate array (legacy format), convert to GeoJSON
    if (Array.isArray(geoJson) && geoJson.length > 0) {
      // Assume it's a polygon coordinate array
      if (Array.isArray(geoJson[0]) && typeof geoJson[0][0] === 'number') {
        // Close the polygon if it's not already closed
        const coords =
          geoJson[0][0] !== geoJson[geoJson.length - 1][0] ||
          geoJson[0][1] !== geoJson[geoJson.length - 1][1]
            ? [...geoJson, geoJson[0]]
            : geoJson

        geometry = {
          type: 'Polygon',
          coordinates: [coords],
        }
      }
    }

    // If it's a Feature, extract the geometry
    if (geometry.type === 'Feature') {
      geometry = geometry.geometry
    }

    // Use Turf.js to calculate the bounding box
    const bbox = turf.bbox(geometry)

    // Convert Turf bbox [minX, minY, maxX, maxY] to Mapbox format [[minX, minY], [maxX, maxY]]
    return [
      [bbox[0], bbox[1]], // Southwest
      [bbox[2], bbox[3]], // Northeast
    ]
  } catch (error) {
    console.warn('Failed to create bounding box from GeoJSON:', error)

    // Fallback: try to extract coordinates manually
    if (geoJson.type === 'Point') {
      return createBoundingBoxFromCenter(
        geoJson.coordinates as [number, number],
        100
      )
    }

    return null
  }
}

/**
 * Creates a bounding box suitable for a property or building analysis
 * Can use either center point or GeoJSON geometry
 */
export const createAnalysisBounds = (
  /** Primary location (property/building center) */
  center: [number, number],
  /** Type of analysis */
  analysisType: 'land' | 'building' | 'estimate' | 'custom',
  /** Custom radius in meters (for custom type) */
  customRadius?: number,
  /** Optional GeoJSON geometry to create bounds from */
  geoJson?: any
): mapboxgl.LngLatBoundsLike => {
  // If GeoJSON is provided, try to create bounds from it first
  if (geoJson) {
    const geoJsonBounds = createBoundingBoxFromGeoJSON(geoJson)

    if (geoJsonBounds) {
      // Return the tight bounding box without expansion
      // The Map component's padding parameter will provide visual spacing
      return geoJsonBounds
    }
  }

  // Fallback to center-based bounds
  let radius: number

  switch (analysisType) {
    case 'land':
      radius = 500 // 500m radius for land analysis
      break
    case 'building':
      radius = 200 // 200m radius for building analysis
      break
    case 'estimate':
      radius = 300 // 300m radius for estimate analysis
      break
    case 'custom':
      radius = customRadius || 500
      break
    default:
      radius = 500
  }

  return createBoundingBoxFromCenter(center, radius)
}
